import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../components/ui/sheet";
import { toast } from "sonner";
import { ChatCircleDots, UserPlus, Phone, EnvelopeSimple, WarningCircle } from "@phosphor-icons/react";

const STATUSES = ["new", "qualified", "contacted", "converted", "lost"];
const STATUS_COLOR = {
  new: "bg-blue-100 text-blue-700", qualified: "bg-indigo-100 text-indigo-700",
  contacted: "bg-amber-100 text-amber-700", converted: "bg-green-100 text-green-700",
  lost: "bg-gray-200 text-gray-600",
};
const SOURCE_COLOR = {
  chatbot: "bg-violet-100 text-violet-700", website: "bg-sky-100 text-sky-700",
  whatsapp: "bg-green-100 text-green-700", manual: "bg-gray-100 text-gray-600",
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState("");
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    try {
      const [{ data }, { data: s }] = await Promise.all([
        api.get(`/leads${filter ? `?status=${filter}` : ""}`),
        api.get("/leads/stats"),
      ]);
      setLeads(data); setStats(s);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (lid, status) => {
    try { await api.patch(`/leads/${lid}`, { status }); toast.success("Status updated"); load(); if (sel?.id === lid) setSel({ ...sel, status }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try { const { data } = await api.patch(`/leads/${sel.id}`, { note }); setSel(data); setNote(""); toast.success("Note added"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <PageHeader title="Leads" subtitle="Enquiries from the AI chatbot, website & campaigns" />
      <div className="p-6 space-y-5" data-testid="leads-page">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[["Total", stats.total, ""], ["New", stats.new, "new"], ["Qualified", stats.qualified, "qualified"],
            ["Contacted", stats.contacted, "contacted"], ["Converted", stats.converted, "converted"], ["Needs Human", stats.needs_human, ""]].map(([label, val, f], i) => (
            <button key={i} onClick={() => setFilter(f)} data-testid={`lead-stat-${label.toLowerCase().replace(" ", "-")}`}
              className={`text-left rounded-sm border p-3 transition ${filter === f && f ? "border-primary ring-1 ring-primary" : "border-border"} ${label === "Needs Human" && val > 0 ? "bg-red-50 border-red-200" : "bg-card"}`}>
              <p className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1">{label === "Needs Human" && <WarningCircle size={13} weight="fill" className="text-red-500" />}{label}</p>
              <p className="text-2xl font-bold font-heading">{val ?? 0}</p>
            </button>
          ))}
        </div>

        <div className="border border-border rounded-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead>City / Pin</TableHead>
                <TableHead>Interest</TableHead><TableHead>Source</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No leads yet</TableCell></TableRow>}
              {leads.map((l) => (
                <TableRow key={l.id} className="hover:bg-secondary/30">
                  <TableCell className="font-semibold">{l.name || "—"}{l.needs_human && <span className="ml-1.5 text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded">needs human</span>}</TableCell>
                  <TableCell className="text-sm">{l.phone && <span className="flex items-center gap-1"><Phone size={12} />{l.phone}</span>}{l.email && <span className="flex items-center gap-1 text-muted-foreground text-xs"><EnvelopeSimple size={12} />{l.email}</span>}</TableCell>
                  <TableCell className="text-sm">{l.city || "—"}{l.pincode ? ` · ${l.pincode}` : ""}</TableCell>
                  <TableCell className="text-sm max-w-[200px] truncate">{l.service_interest || "—"}</TableCell>
                  <TableCell><span className={`text-xs px-2 py-0.5 rounded-sm font-medium capitalize ${SOURCE_COLOR[l.source] || "bg-gray-100"}`}>{l.source}</span></TableCell>
                  <TableCell>
                    <Select value={l.status} onValueChange={(v) => setStatus(l.id, v)}>
                      <SelectTrigger className={`h-7 w-[120px] rounded-sm text-xs border-0 ${STATUS_COLOR[l.status]}`} data-testid={`lead-status-${l.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right"><Button variant="ghost" size="sm" className="text-primary" onClick={() => setSel(l)} data-testid={`lead-view-${l.id}`}>View</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Sheet open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="lead-detail">
          <SheetHeader>
            <SheetTitle className="font-heading flex items-center gap-2"><UserPlus size={18} /> {sel?.name || "Lead"}</SheetTitle>
            <SheetDescription>Lead from {sel?.source}. Update status, assign and add notes.</SheetDescription>
          </SheetHeader>
          {sel && (
            <div className="py-5 space-y-4 text-sm">
              <div className="border border-border rounded-sm divide-y divide-border">
                {[["Phone", sel.phone], ["Email", sel.email], ["City", sel.city], ["Pincode", sel.pincode], ["Interest", sel.service_interest], ["Source", sel.source]].map(([k, v]) => (
                  <div key={k} className="flex justify-between px-3 py-2"><span className="text-xs uppercase text-muted-foreground">{k}</span><span className="text-right">{v || "—"}</span></div>
                ))}
              </div>
              <div>
                <p className="text-xs uppercase text-muted-foreground mb-1">Status</p>
                <Select value={sel.status} onValueChange={(v) => setStatus(sel.id, v)}>
                  <SelectTrigger className="rounded-sm h-9" data-testid="lead-detail-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-xs uppercase text-muted-foreground mb-1">Notes</p>
                <div className="space-y-2 mb-2 max-h-48 overflow-y-auto">
                  {(sel.notes || []).map((n, i) => (
                    <div key={i} className="bg-secondary/40 rounded-sm p-2 text-xs"><span className="font-semibold">{n.by}</span>: {n.text}</div>
                  ))}
                  {(!sel.notes || sel.notes.length === 0) && <p className="text-xs text-muted-foreground">No notes yet</p>}
                </div>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a follow-up note…" className="rounded-sm" data-testid="lead-note-input" />
                <Button size="sm" className="rounded-sm mt-2" onClick={addNote} data-testid="lead-add-note">Add Note</Button>
              </div>
              {sel.phone && <a href={`https://wa.me/91${sel.phone.replace(/\D/g, "").slice(-10)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-green-700 hover:underline"><ChatCircleDots size={16} /> Message on WhatsApp</a>}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
